import { Badge } from '@/components/shadcn/badge'
import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { RestSchemas } from '@/contexts/QueryProvider'
import { ColumnDef } from '@tanstack/react-table'
import {
  coerceRequestStatus,
  getRequestStatusLabel,
  getTicketTypeLabel,
  REQUEST_STATUS_CLOSED,
} from '../Requests/types'
import { RequestRowActions } from './RequestRowActions'
import { formatCreatedBy, getTicketSubject } from '../../Platform/PlatformTicketRequestsPage/requestDetailUtils'
import { useTicketLookupMaps } from '../../Platform/PlatformTicketRequestsPage/TicketLookupsContext'

type ExistingRequest = RestSchemas['Messages.Requests.ExistingRequest'] & { subject?: string | null }

function SubjectCell({ request }: { request: ExistingRequest }) {
  const lookups = useTicketLookupMaps()
  return (
    <>
      {getTicketSubject(
        request.ticketType,
        request.ticket,
        request.subject,
        lookups.projectNames,
        lookups.applicationNames
      )}
    </>
  )
}

function CreatedByCell({ userId }: { userId?: number | null }) {
  const lookups = useTicketLookupMaps()
  return <>{formatCreatedBy(userId, lookups)}</>
}

export function getOrganisationRequestColumns(
  onViewDetails: (request: ExistingRequest) => void
): ColumnDef<ExistingRequest>[] {
  return [
    {
      id: 'ticketType',
      accessorKey: 'ticketType',
      header: ({ column }) => <SortingHeaderButton column={column}>Type</SortingHeaderButton>,
      cell: ({ row }) => getTicketTypeLabel(row.original.ticketType),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <SortingHeaderButton column={column}>Status</SortingHeaderButton>,
      cell: ({ row }) => (
        <Badge variant={coerceRequestStatus(row.original.status) === REQUEST_STATUS_CLOSED ? 'secondary' : 'default'}>
          {getRequestStatusLabel(row.original.status)}
        </Badge>
      ),
    },
    {
      id: 'subject',
      accessorFn: (row) => row.subject ?? '',
      header: ({ column }) => <SortingHeaderButton column={column}>Name</SortingHeaderButton>,
      cell: ({ row }) => <SubjectCell request={row.original} />,
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => <SortingHeaderButton column={column}>Created</SortingHeaderButton>,
      cell: ({ row }) => (row.original.createdAt ? new Date(row.original.createdAt).toLocaleString() : ''),
    },
    {
      id: 'createdBy',
      accessorKey: 'createdBy',
      header: 'Created by',
      cell: ({ row }) => <CreatedByCell userId={row.original.createdBy} />,
    },
    {
      id: 'actions',
      meta: { disableCellTruncate: true },
      size: 72,
      cell: ({ row }) => (
        <div className="flex justify-end pr-5">
          <RequestRowActions
            request={row.original}
            listScope="organisation"
            onViewDetails={onViewDetails}
          />
        </div>
      ),
    },
  ]
}
